import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import Title1 from '../../components/title/title1';
import Title3 from '../../components/title/title3';
import { useTranslation } from 'react-i18next';
import contactData from '../../data/company.json';
import faqData from '../../data/faq.json';
import { Link } from 'react-router-dom';
import SectionLayout from '../../layouts/section-layout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';

const contactLocales = "contact";

const ContactView = () => {
    const theme = useTheme();
    const {t} = useTranslation();

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <>
            <Stack
                direction='column'
                width='100%'
            >
                {/* Contact Details */}
                <SectionLayout children={
                    <Grid
                        direction='row'
                        container
                        spacing={{xs: 2, md: 5}}
                    >
                        <Grid
                            size={{xs: 12, md: 6}}
                        >
                            <Title3 
                                title={t(`${contactLocales}.title`)}
                                description={t(`${contactLocales}.description`)}
                                chipIcon={<ContactMailIcon color='white'/>}
                                chipLabel={t(`${contactLocales}.chipTitle`)}
                                chipColor="#78b31a"
                            />
                        </Grid>

                        <Grid
                            size={{xs: 6, md: 3}}
                            direction='row'
                            justifyContent='start'
                        >
                            <ContactDetails 
                                title={t(`${contactLocales}.addressTitle`)}
                                icon={<LocationOnIcon sx={{color: '#ffa103', width: 25, height: 25}}/>}
                                iconBoxColor="#ffa3033a"
                                children={
                                    <Stack
                                        direction='column'
                                        alignItems='start'
                                    >
                                        {
                                            [`no`, `road`,`city`, `country`].map((item, index) => {
                                                return <Typography
                                                    key={index}
                                                    fontSize={{xs: 12, md: 14}}
                                                    fontWeight={400}
                                                    textAlign={{xs: 'center', md: 'left'}}
                                                    lineHeight={1.5}
                                                    pb={0.5}
                                                >
                                                    {`${t(`footer.address1.${item}`)}${index < 3 ? ',' : ''}`}
                                                </Typography>
                                            })
                                        }
                                    </Stack>
                                }
                            />
                        </Grid>

                        <Grid
                            size={{xs: 6, md: 3}}
                            direction='row'
                            justifyContent='start'
                        >
                            <ContactDetails 
                                title={t(`${contactLocales}.contactTitle`)}
                                icon={<PermPhoneMsgIcon sx={{color: '#8dc909', width: 25, height: 25}}/>}
                                iconBoxColor="#8dc9093a"
                                children={
                                    <Stack
                                        direction='column'
                                        alignItems='start'
                                    >
                                        {
                                            [contactData.socialMedia[0], contactData.socialMedia[1]].map((item, index) => {
                                                return <Link
                                                    to={item.link}
                                                    target='_blank'
                                                    style={{
                                                        textDecoration: 'none'
                                                    }}
                                                >
                                                    <Typography
                                                        key={index}
                                                        fontSize={{xs: 12, md: 14}}
                                                        fontWeight={400}
                                                        textAlign='left'
                                                        lineHeight={1.5}
                                                        pb={0.5}
                                                        color={theme.palette.text.primary}
                                                        sx={{
                                                            '&: hover': {
                                                                transform: 'scale(0.95)'
                                                            }
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                </Link>
                                            })
                                        }
                                    </Stack>
                                }
                            />
                        </Grid>
                    </Grid>
                }/>

                {/* Google Map */}
                <Box
                    className="sectionDoubleSpacing"
                    sx={{
                        backgroundColor: theme.palette.background.paper
                    }}
                >
                    <iframe 
                        src={contactData.embeddedMapLink} 
                        width='100%'
                        height={isMobile ? "200" : "450"} 
                        style={{
                            border: 0
                        }}
                        loading="lazy" 
                    ></iframe>
                </Box>

                {/* Faq */}
                {/* <Box
                    className=""
                    sx={{
                        backgroundColor: theme.palette.background.paper
                    }}
                >
                    <Stack
                        direction='column'
                        alignItems='center'
                        className="bodySideMargin1  sectionRadius sectionInSidePadding"
                        sx={{
                            backgroundColor: theme.palette.background.default
                        }}
                    >
                        <Title1 
                            title={t(`${contactLocales}.faq.title`)}
                            description={t(`${contactLocales}.faq.description`)}
                        />

                        {
                            faqData.en.map((item, index) => {
                                return <FaqDetails 
                                    key={index}
                                    question={item.question}
                                    answer={item.answer}
                                    expanded={index === 0}
                                />
                            })
                        }
                    </Stack>
                </Box> */}
            </Stack>
        </>
    );
}

export default ContactView;

const ContactDetails = ({
    title,
    children,
    icon,
    iconBoxColor
}) => {
    return (
        <>
            <Stack
                direction='column'
                alignItems='start'
                data-aos="zoom-in"
            >
                <Box
                    sx={{
                        p: 1,
                        backgroundColor: iconBoxColor,
                        borderRadius: 2,
                        mb: 1
                    }}
                >
                    {icon}
                </Box>

                <Typography
                    fontSize={{xs: 14, lg: 16}}
                    fontWeight={700}
                    textAlign='left'
                    lineHeight={1.5}
                    pb={1.5}
                >
                    {title}
                </Typography>

                {children}
            </Stack>
        </>
    );
}

const FaqDetails = ({
    question,
    answer,
    expanded=false
}) => {
    const theme = useTheme();

    return (
        <>
            <Accordion
                elevation={0}
                sx={{
                    width: '100%',
                    backgroundColor: theme.palette.background.default,
                    py: {xs: 0, md: 1},
                    '&:before': {
                        display: 'none',
                    },
                    borderBottom: `2px solid ${theme.palette.background.paper}`
                }}
                defaultExpanded={expanded}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    <Typography
                        fontWeight={600}
                        fontSize={{xs: 12, md: 16}}
                        textAlign='left'
                        color={theme.palette.text.primary}
                        lineHeight={1.5}
                    >
                        {question}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Typography
                        fontWeight={500}
                        fontSize={{xs: 12, md: 14}}
                        textAlign='left'
                        lineHeight={1.5}
                        color={theme.palette.text.secondary}
                    >
                        {answer}
                    </Typography>
                </AccordionDetails>
            </Accordion>
        </>
    );
}